<template>
    <div class="location-performance-table">
        <div class="table-header">
            <h2 class="table-title">Location Performance</h2>
            <p class="table-subtitle">Detailed metrics across all locations</p>
        </div>

        <div class="table-controls">
            <div class="search-input">
                <i class="fas fa-search"></i>
                <input type="text" v-model="searchQuery" placeholder="Search locations..." class="search-field" />
            </div>

            <div class="filter-group">
                <label class="filter-label">Region:</label>
                <select v-model="selectedRegion" class="region-select">
                    <option value="">All Regions</option>
                    <option v-for="region in availableRegions" :key="region" :value="region">
                        {{ region }}
                    </option>
                </select>
            </div>

            <div class="results-count">
                {{ filteredLocations.length }} of {{ locations.length }} locations
            </div>
        </div>

        <div class="table-wrapper">
            <table class="performance-table">
                <thead>
                    <tr>
                        <th @click="sortBy('city')" class="sortable">
                            <span class="th-content">
                                Location
                                <i :class="getSortIcon('city')"></i>
                            </span>
                        </th>
                        <th @click="sortBy('region')" class="sortable">
                            <span class="th-content">
                                Region
                                <i :class="getSortIcon('region')"></i>
                            </span>
                        </th>
                        <th @click="sortBy('applicants')" class="sortable text-right">
                            <span class="th-content">
                                Applicants
                                <i :class="getSortIcon('applicants')"></i>
                            </span>
                        </th>
                        <th @click="sortBy('hires')" class="sortable text-right">
                            <span class="th-content">
                                Hires
                                <i :class="getSortIcon('hires')"></i>
                            </span>
                        </th>
                        <th @click="sortBy('openRoles')" class="sortable text-right">
                            <span class="th-content">
                                Current Open
                                <i :class="getSortIcon('openRoles')"></i>
                            </span>
                        </th>
                        <th @click="sortBy('newCreated')" class="sortable text-right">
                            <span class="th-content">
                                New Created
                                <i :class="getSortIcon('newCreated')"></i>
                            </span>
                        </th>
                        <th @click="sortBy('conversionRate')" class="sortable text-right">
                            <span class="th-content">
                                Conversion
                                <i :class="getSortIcon('conversionRate')"></i>
                            </span>
                        </th>
                        <th @click="sortBy('avgTimeToHire')" class="sortable text-right">
                            <span class="th-content">
                                Avg Time to Hire
                                <i :class="getSortIcon('avgTimeToHire')"></i>
                            </span>
                        </th>
                        <th @click="sortBy('agingCandidates')" class="sortable text-right">
                            <span class="th-content">
                                Aging Candidates
                                <i :class="getSortIcon('agingCandidates')"></i>
                            </span>
                        </th>
                        <th @click="sortBy('payRange')" class="sortable text-right">
                            <span class="th-content">
                                Pay Range
                                <i :class="getSortIcon('payRange')"></i>
                            </span>
                        </th>
                        <th class="text-center">Actions</th>
                    </tr>
                </thead>
                <tbody v-if="filteredLocations.length > 0">
                    <tr v-for="location in sortedLocations" :key="location.company_id" class="table-row">
                        <td class="location-city-state">
                            <span v-if="location.city || location.state">
                                {{ location.city }}{{ location.city && location.state ? ', ' : '' }}{{ location.state }}
                            </span>
                            <span v-else class="text-muted">—</span>
                        </td>
                        <td>
                            <span class="region-badge">{{ location.group_name }}</span>
                        </td>
                        <td class="text-right">
                            <span class="metric-value">{{ formatNumber(location.applicants?.total) }}</span>
                        </td>
                        <td class="text-right">
                            <span class="metric-value">{{ formatNumber(location.hires?.total) }}</span>
                        </td>
                        <td class="text-right">
                            <span
                                class="open-roles-count"
                                :class="getOpenRolesClass(location.roles?.open)"
                                @click="handleOpenRolesClick(location)"
                            >
                                {{ formatNumber(location.roles?.open) }}
                            </span>
                        </td>
                        <td class="text-right">
                            <span class="new-created-count">
                                {{ formatNumber(location.roles?.new_created) }}
                            </span>
                        </td>
                        <td class="text-right">
                            <span class="conversion-rate" :class="getConversionClass(getConversionRate(location))">
                                {{ formatPercent(getConversionRate(location)) }}
                            </span>
                        </td>
                        <td class="text-right">
                            <span class="time-to-hire" :class="getTimeToHireClass(location.hires?.avg_time_to_hire)">
                                {{ formatDays(location.hires?.avg_time_to_hire) }}
                            </span>
                        </td>
                        <td class="text-right">
                            <span class="aging-count" :class="getAgingClass(location.roles?.aging_candidates)">
                                {{ formatNumber(location.roles?.aging_candidates) }}
                            </span>
                        </td>
                        <td class="text-right">
                            <span class="pay-range-value">
                                {{ location.pay_range?.formatted || '—' }}
                            </span>
                        </td>
                        <td class="text-center">
                            <button @click="handleViewDetails(location)" class="view-details-btn">
                                <i class="fas fa-chart-line"></i>
                                View Details
                            </button>
                        </td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr>
                        <td colspan="11" class="empty-state">
                            <div class="empty-state-content">
                                <i class="fas fa-search empty-icon"></i>
                                <p class="empty-title">No locations found</p>
                                <p class="empty-subtitle">
                                    Try adjusting your search or filter criteria
                                </p>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="locations.length === 0 && !loading" class="no-data-state">
            <i class="fas fa-database no-data-icon"></i>
            <p class="no-data-title">No location data available</p>
            <p class="no-data-subtitle">Location analytics will appear here once data is available</p>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";

const props = defineProps({
  locations: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["open-roles-modal"]);

const router = useRouter();
const searchQuery = ref("");
const selectedRegion = ref("");
const sortColumn = ref("name");
const sortDirection = ref("asc");

const availableRegions = computed(() => {
  const regions = props.locations.map(loc => loc.group_name).filter(Boolean);
  return [...new Set(regions)].sort();
});

const filteredLocations = computed(() => {
  let filtered = props.locations;

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(loc =>
      loc.company_name?.toLowerCase().includes(query)
    );
  }

  if (selectedRegion.value) {
    filtered = filtered.filter(loc => loc.group_name === selectedRegion.value);
  }

  return filtered;
});

const sortedLocations = computed(() => {
  const sorted = [...filteredLocations.value];

  sorted.sort((a, b) => {
    let aVal = a[sortColumn.value];
    let bVal = b[sortColumn.value];

    if (sortColumn.value === "payRange") {
      aVal = a.pay_range?.min;
      bVal = b.pay_range?.min;
    }

    if (sortColumn.value === "openRoles") {
      aVal = a.roles?.open;
      bVal = b.roles?.open;
    }

    if (sortColumn.value === "newCreated") {
      aVal = a.roles?.new_created;
      bVal = b.roles?.new_created;
    }

    if (aVal == null) return 1;
    if (bVal == null) return -1;

    if (typeof aVal === "string") {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    if (aVal < bVal) return sortDirection.value === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection.value === "asc" ? 1 : -1;
    return 0;
  });

  return sorted;
});

function sortBy(column) {
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortColumn.value = column;
    sortDirection.value = "asc";
  }
}

function getSortIcon(column) {
  if (sortColumn.value !== column) {
    return "fas fa-sort sort-icon";
  }
  return sortDirection.value === "asc"
    ? "fas fa-sort-up sort-icon active"
    : "fas fa-sort-down sort-icon active";
}

function handleViewDetails(location) {
  router.push({
    name: "enterprise-location-analytics",
    params: { companyId: location.company_id }
  });
}

function getConversionRate(location) {
  const applicants = location.applicants?.total || 0;
  const hires = location.hires?.total || 0;
  if (applicants === 0) return null;
  return (hires / applicants) * 100;
}

function formatNumber(value) {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US").format(value);
}

function formatPercent(value) {
  if (value == null) return "—";
  return `${value.toFixed(1)}%`;
}

function formatDays(value) {
  if (value == null) return "—";
  const days = Math.round(value);
  return `${days} ${days === 1 ? "day" : "days"}`;
}

function getConversionClass(rate) {
  if (rate == null) return "";
  if (rate >= 15) return "rate-high";
  if (rate >= 10) return "rate-medium";
  return "rate-low";
}

function getTimeToHireClass(days) {
  if (days == null) return "";
  if (days <= 30) return "time-good";
  if (days <= 45) return "time-medium";
  return "time-slow";
}

function getAgingClass(count) {
  if (count == null || count === 0) return "";
  if (count >= 10) return "aging-high";
  if (count >= 5) return "aging-medium";
  return "aging-low";
}

function getOpenRolesClass(count) {
  if (count == null || count === 0) return "open-roles-zero";
  return "open-roles-clickable";
}

function handleOpenRolesClick(location) {
  const count = location.roles?.open || 0;
  if (count > 0) {
    emit("open-roles-modal", {
      companyId: location.company_id,
      companyName: location.company_name,
      city: location.city,
      state: location.state,
      count: count
    });
  }
}

</script>

<style scoped>
.location-performance-table {
    background: white;
    border-radius: 0.75rem;
    padding: 2rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.table-header {
    margin-bottom: 1.5rem;
}

.table-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
}

.table-subtitle {
    margin: 0.25rem 0 0;
    font-size: 0.875rem;
    color: #6b7280;
}

.table-controls {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
}

.search-input {
    position: relative;
    flex: 1;
    min-width: 250px;
}

.search-input i {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    pointer-events: none;
}

.search-field {
    width: 100%;
    padding: 0.625rem 1rem 0.625rem 2.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    transition: all 0.2s;
}

.search-field:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.filter-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.filter-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
}

.region-select {
    padding: 0.625rem 2.5rem 0.625rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    background-color: white;
    background-position: right 0.5rem center;
    background-repeat: no-repeat;
    background-size: 1.25rem;
    appearance: none;
    cursor: pointer;
    transition: all 0.2s;
}

.region-select:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.results-count {
    margin-left: auto;
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
}

.table-wrapper {
    overflow-x: auto;
    margin: 0 -2rem;
    padding: 0 2rem;
}

.performance-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
}

.performance-table thead {
    background-color: #f9fafb;
    border-top: 1px solid #e5e7eb;
    border-bottom: 2px solid #e5e7eb;
}

.performance-table th {
    padding: 0.875rem 1rem;
    text-align: left;
    font-weight: 600;
    color: #374151;
    font-size: 0.8125rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    white-space: nowrap;
}

.performance-table th.sortable {
    cursor: pointer;
    user-select: none;
    transition: background-color 0.15s;
}

.performance-table th.sortable:hover {
    background-color: #f3f4f6;
}

.th-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.sort-icon {
    opacity: 0.3;
    font-size: 0.75rem;
    transition: opacity 0.2s;
}

.sort-icon.active {
    opacity: 1;
    color: #3b82f6;
}

.text-right {
    text-align: right;
}

.text-right .th-content {
    justify-content: flex-end;
}

.text-center {
    text-align: center;
}

.performance-table tbody tr {
    border-bottom: 1px solid #e5e7eb;
    transition: background-color 0.15s;
}

.performance-table tbody tr:hover {
    background-color: #f9fafb;
}

.performance-table tbody tr:last-child {
    border-bottom: none;
}

.performance-table td {
    padding: 1rem;
    color: #111827;
}

.location-info {
    display: flex;
    align-items: center;
    gap: 0.625rem;
}

.location-icon {
    color: #3b82f6;
    font-size: 1rem;
}

.location-name {
    font-weight: 600;
}

.location-city-state {
    color: #6b7280;
    font-size: 0.875rem;
}

.text-muted {
    color: #9ca3af;
}

.region-badge {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    background-color: #eff6ff;
    color: #1e40af;
    border-radius: 9999px;
    font-size: 0.8125rem;
    font-weight: 500;
    white-space: nowrap;
}

.metric-value {
    font-weight: 500;
    color: #374151;
}

.conversion-rate {
    display: inline-block;
    padding: 0.25rem 0.625rem;
    border-radius: 0.375rem;
    font-weight: 600;
}

.rate-high {
    background-color: #dcfce7;
    color: #166534;
}

.rate-medium {
    background-color: #fef3c7;
    color: #92400e;
}

.rate-low {
    background-color: #fee2e2;
    color: #991b1b;
}

.time-to-hire {
    font-weight: 500;
}

.time-good {
    color: #22c55e;
}

.time-medium {
    color: #f59e0b;
}

.time-slow {
    color: #ef4444;
}

.aging-count {
    font-weight: 600;
}

.aging-high {
    color: #ef4444;
}

.aging-medium {
    color: #f59e0b;
}

.aging-low {
    color: #6b7280;
}

.pay-range-value {
    font-weight: 500;
    color: #059669;
    white-space: nowrap;
}

.open-roles-count {
    font-weight: 600;
    transition: all 0.2s;
}

.open-roles-clickable {
    color: #3b82f6;
    cursor: pointer;
}

.open-roles-clickable:hover {
    color: #2563eb;
    text-decoration: underline;
}

.open-roles-zero {
    color: #9ca3af;
    cursor: default;
}

.new-created-count {
    font-weight: 500;
    color: #6b7280;
}

.view-details-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background-color: #3b82f6;
    color: white;
    border: none;
    border-radius: 0.375rem;
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.view-details-btn:hover {
    background-color: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.view-details-btn i {
    font-size: 0.75rem;
}

.empty-state {
    padding: 4rem 2rem !important;
}

.empty-state-content {
    text-align: center;
}

.empty-icon {
    font-size: 3rem;
    color: #d1d5db;
    margin-bottom: 1rem;
}

.empty-title {
    margin: 0 0 0.5rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: #374151;
}

.empty-subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
}

.no-data-state {
    text-align: center;
    padding: 4rem 2rem;
}

.no-data-icon {
    font-size: 3rem;
    color: #d1d5db;
    margin-bottom: 1rem;
}

.no-data-title {
    margin: 0 0 0.5rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: #374151;
}

.no-data-subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
}

@media (max-width: 1024px) {
    .table-wrapper {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    .performance-table {
        min-width: 900px;
    }
}

@media (max-width: 640px) {
    .location-performance-table {
        padding: 1rem;
    }

    .table-controls {
        flex-direction: column;
        align-items: stretch;
    }

    .search-input {
        min-width: 100%;
    }

    .filter-group {
        width: 100%;
    }

    .region-select {
        flex: 1;
    }

    .results-count {
        margin-left: 0;
        text-align: center;
    }

    .table-wrapper {
        margin: 0 -1rem;
        padding: 0 1rem;
    }
}
</style>
